import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Search, Briefcase, Plus, Check, NotebookText } from 'lucide-react';
import { projectId, API_BASE } from '../utils/supabase/info';
import { useDebounce } from '../utils/hooks/useDebounce';
import { NORWEGIAN_COUNTIES } from '../utils/norwegian-locations';
import { motion, AnimatePresence } from 'motion/react';
import { AnimatedFilterIcon, type AnimatedFilterIconHandle } from './AnimatedFilterIcon';
import { GigCard } from './GigCard';
import { GigDetailModal } from './GigDetailModal';
import { ShowInterestModal } from './ShowInterestModal';
import { CreateGigModal } from './CreateGigModal';
import { PageShell } from './layout/PageShell';
import { toast } from '../utils/toast';
import { devLog } from '../utils/logger';
import { fetchWithAuth } from '../utils/api';
import { Loader } from './ui/Loader';
import { canCreateGig, canShowGigInterest } from '../utils/accessPolicy';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { useWindowVirtualizer } from '@tanstack/react-virtual';

const HEADER_TEXTS = [
  "Finn ditt neste oppdrag",
  "Søk etter spennende prosjekter",
  "Utforsk nye muligheter",
  "Finn ditt neste prosjekt",
  "Søk blant oppdrag",
  "Oppdag nye utfordringer",
];

interface GigsPageProps {
  accessToken: string | null;
  /** Current logged-in user id (from session) – used to detect own gigs */
  currentUserId?: string;
  /** When true (desktop), CreateGigModal is portaled to body so page is visible behind */
  isDesktop?: boolean;
  onViewProfile: (userId: string) => void;
  onLoginRequired?: () => void;
  userProfile?: any;
  onOpenMyInterests?: () => void;
  onOpenGigInterests?: (gigId: string, gigTitle: string) => void;
  createGigModalTrigger?: { gigIdToEdit?: string; openInView?: 'home' | 'gigs' } | null;
  onClearCreateGigModalTrigger?: () => void;
  onNavigateToSubscription?: () => void;
}

export function GigsPage({ 
  accessToken, 
  currentUserId,
  isDesktop = false,
  onViewProfile, 
  onLoginRequired, 
  userProfile,
  onOpenMyInterests,
  onOpenGigInterests,
  createGigModalTrigger,
  onClearCreateGigModalTrigger,
  onNavigateToSubscription,
}: GigsPageProps) {
  const [gigs, setGigs] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshingList, setRefreshingList] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const enableListVirtualization = (import.meta as any)?.env?.VITE_ENABLE_LIST_VIRTUALIZATION === 'true';
  const enableTrueVirtualization = (import.meta as any)?.env?.VITE_ENABLE_TRUE_VIRTUALIZATION === 'true';
  const [virtualGigLimit, setVirtualGigLimit] = useState(120);
  const [isMobile, setIsMobile] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const countyDropdownRef = useRef<HTMLDivElement>(null);
  const filterIconRef = useRef<AnimatedFilterIconHandle>(null);
  const hasFetchedInitial = useRef(false);
  const gigsFetchRequestIdRef = useRef(0);
  const gigsFetchAbortRef = useRef<AbortController | null>(null);
  const gigsLoadMoreAbortRef = useRef<AbortController | null>(null);
  const gigsCountRef = useRef(0);
  const lastGigsBackgroundRefreshAtRef = useRef(0);
  const gigInterestsFetchInFlightRef = useRef(false);
  const lastGigInterestsFetchAtRef = useRef(0);
  const gigDetailFetchRequestIdRef = useRef(0);
  const editGigFetchRequestIdRef = useRef(0);
  // iOS PWA: JS-based sticky (position:sticky is broken in standalone mode)
  // Uses direct DOM manipulation (no React state) to avoid feedback-loop between
  // IntersectionObserver → setState → re-render → layout shift → observer fires again.
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyBarRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const [headerText] = useState(() => {
    return HEADER_TEXTS[Math.floor(Math.random() * HEADER_TEXTS.length)];
  });

  // Check if mobile on mount and resize (used to safely enable single-column windowing).
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1280);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Gig modals state
  const [selectedGig, setSelectedGig] = useState<any>(null);
  const [showGigDetailModal, setShowGigDetailModal] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestGigId, setInterestGigId] = useState<string>('');
  const [interestGigTitle, setInterestGigTitle] = useState<string>('');
  const [showCreateGigModal, setShowCreateGigModal] = useState(false);
  const [gigInterests, setGigInterests] = useState<Record<string, boolean>>({});
  const canOpenMyInterests = canShowGigInterest(userProfile);

  useModalFocus(showCreateGigModal || showGigDetailModal, { fullscreen: true });

  // iOS PWA: IntersectionObserver-based sticky (position:sticky broken in standalone mode)
  // Direct DOM manipulation avoids React re-render loop that causes scroll freeze/thrashing.
  useEffect(() => {
    const isIosPwa = document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;

    const sentinel = stickySentinelRef.current;
    const bar = stickyBarRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;

    // Header bottom = fixed header height (includes safe-area via padding)
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;

    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return; // no change — skip DOM work
        isStuck = shouldStick;
        if (shouldStick) {
          // Measure height NOW (not at mount) so it reflects current layout
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          const barH = bar.offsetHeight + mb;
          placeholder.style.height = `${barH}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // iOS PWA guard: if sticky bar content height changes while already stuck,
  // keep placeholder height in sync to avoid content jump/overlap.
  useEffect(() => {
    const isIosPwa = document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const bar = stickyBarRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!bar || !placeholder || !bar.classList.contains('is-stuck')) return;
    const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
    placeholder.style.height = `${bar.offsetHeight + mb}px`;
  }, [canOpenMyInterests]);

  // Listen to createGigModalTrigger from App.tsx
  useEffect(() => {
    if (createGigModalTrigger) {
      const { gigIdToEdit } = createGigModalTrigger;
      
      if (gigIdToEdit) {
        const listGig = gigs.find((g) => g.id === gigIdToEdit) ?? null;
        if (listGig) {
          setSelectedGig(listGig);
          setShowCreateGigModal(true);
        }

        const requestId = ++editGigFetchRequestIdRef.current;
        // Fetch fresh gig data in background to avoid modal-open delay.
        (async () => {
          if (!accessToken) return;
          
          try {
            const response = await fetch(
              `${API_BASE}/gigs/${gigIdToEdit}`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );

            if (response.ok) {
              const data = await response.json();
              if (requestId !== editGigFetchRequestIdRef.current) return;
              setSelectedGig(data.gig);
              setShowCreateGigModal(true);
            } else if (!listGig) {
              toast.error('Kunne ikke hente oppdrag');
            }
          } catch (error) {
            if (!listGig) {
              toast.error('Kunne ikke hente oppdrag');
            }
          }
        })();
      } else {
        // Open create modal
        setShowCreateGigModal(true);
      }
      
      // Clear trigger after opening modal
      if (onClearCreateGigModalTrigger) {
        onClearCreateGigModalTrigger();
      }
    }
  }, [accessToken, createGigModalTrigger, gigs, onClearCreateGigModalTrigger]);

  // Animate filter icon based on dropdown state
  useEffect(() => {
    if (filterIconRef.current) {
      if (showCountyDropdown) {
        filterIconRef.current.startAnimation();
      } else {
        filterIconRef.current.stopAnimation();
      }
    }
  }, [showCountyDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (countyDropdownRef.current && !countyDropdownRef.current.contains(event.target as Node)) {
        setShowCountyDropdown(false);
      }
    };

    if (showCountyDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCountyDropdown]);

  // Debounce search query
  const debouncedSearch = useDebounce(searchQuery, 500);
  const BACKGROUND_REFRESH_COOLDOWN_MS = 12000;
  const GIG_INTERESTS_FETCH_COOLDOWN_MS = 10000;

  const buildGigsSearchParams = useCallback((offset: number) => {
    const params = new URLSearchParams();
    const searchTerm = debouncedSearch.trim();
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    if (selectedCounties.length > 0) {
      // Backend now supports multi-county filtering via `counties`.
      params.append('counties', selectedCounties.join(','));
    }
    params.append('limit', '20');
    params.append('offset', String(offset));
    return params;
  }, [debouncedSearch, selectedCounties]);

  const areGigsEquivalentForRender = useCallback((prevGig: any, nextGig: any) => {
    return (
      prevGig?.id === nextGig?.id &&
      prevGig?.updatedAt === nextGig?.updatedAt &&
      prevGig?.status === nextGig?.status &&
      prevGig?.isActive === nextGig?.isActive &&
      prevGig?.interestCount === nextGig?.interestCount &&
      prevGig?.title === nextGig?.title &&
      (prevGig?.imageUrl || prevGig?.image_url || prevGig?.image) ===
        (nextGig?.imageUrl || nextGig?.image_url || nextGig?.image)
    );
  }, []);

  const reconcileGigList = useCallback((prevGigs: any[], nextGigs: any[]) => {
    if (!prevGigs.length) return nextGigs;
    const prevById = new Map(prevGigs.map((gig) => [gig.id, gig]));
    const merged = nextGigs.map((nextGig) => {
      const prevGig = prevById.get(nextGig.id);
      if (!prevGig) return nextGig;
      return areGigsEquivalentForRender(prevGig, nextGig) ? prevGig : nextGig;
    });

    const unchanged =
      prevGigs.length === merged.length &&
      merged.every((gig, index) => gig === prevGigs[index]);

    return unchanged ? prevGigs : merged;
  }, [areGigsEquivalentForRender]);

  useEffect(() => {
    gigsCountRef.current = gigs.length;
  }, [gigs.length]);
  const visibleGigs = useMemo(
    () => (enableListVirtualization ? gigs.slice(0, virtualGigLimit) : gigs),
    [enableListVirtualization, gigs, virtualGigLimit]
  );

  const enableWindowVirtualization = enableListVirtualization && enableTrueVirtualization && isMobile;
  const gigsForRender = enableWindowVirtualization ? gigs : visibleGigs;
  const gigsVirtualizer = useWindowVirtualizer({
    count: enableWindowVirtualization ? gigsForRender.length : 0,
    estimateSize: () => 520,
    overscan: 6,
  });
  const requestBackgroundGigsRefresh = () => {
    const now = Date.now();
    if (now - lastGigsBackgroundRefreshAtRef.current < BACKGROUND_REFRESH_COOLDOWN_MS) {
      return;
    }
    lastGigsBackgroundRefreshAtRef.current = now;
    hasFetchedInitial.current = false;
    void fetchGigs(true, true);
  };

  // Reset pagination when search changes
  useEffect(() => {
    setGigs([]);
    setHasMore(false);
    hasFetchedInitial.current = false;
    fetchGigs(true, false);
  }, [debouncedSearch, selectedCounties]);

  // Refetch when user comes back to the page
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        devLog('👁️ Page visible again - refreshing gigs');
        requestBackgroundGigsRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [debouncedSearch]);

  // Cross-device sync events from App/useUserSync.
  useEffect(() => {
    const handleGigsRefresh = () => {
      requestBackgroundGigsRefresh();
    };
    const handleGigInterestsRefresh = () => {
      if (accessToken) void fetchUserInterests();
    };

    window.addEventListener('talentfunn:gigs-refresh', handleGigsRefresh);
    window.addEventListener('talentfunn:gig-interests-refresh', handleGigInterestsRefresh);
    return () => {
      window.removeEventListener('talentfunn:gigs-refresh', handleGigsRefresh);
      window.removeEventListener('talentfunn:gig-interests-refresh', handleGigInterestsRefresh);
    };
  }, [accessToken, debouncedSearch, selectedCounties]);

  // Infinite scroll observer
  useEffect(() => {
    if (!loadMoreRef.current || loading || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && enableListVirtualization && virtualGigLimit < gigs.length) {
          setVirtualGigLimit((prev) => Math.min(prev + 120, gigs.length));
          return;
        }
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          loadMoreGigs();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(loadMoreRef.current);

    return () => observer.disconnect();
  }, [enableListVirtualization, gigs.length, hasMore, loading, loadingMore, virtualGigLimit]);

  useEffect(() => {
    setVirtualGigLimit(120);
  }, [debouncedSearch, selectedCounties, enableListVirtualization]);

  useEffect(() => {
    return () => {
      gigsFetchAbortRef.current?.abort();
      gigsLoadMoreAbortRef.current?.abort();
    };
  }, []);

  async function fetchGigs(reset: boolean = false, backgroundRefresh: boolean = false) {
    if (hasFetchedInitial.current && !reset) return;
    hasFetchedInitial.current = true;
    const requestId = ++gigsFetchRequestIdRef.current;
    const shouldShowBlockingLoader = !backgroundRefresh || gigsCountRef.current === 0;

    if (shouldShowBlockingLoader) {
      setLoading(true);
      setRefreshingList(false);
    } else {
      setRefreshingList(true);
    }
    try {
      const params = buildGigsSearchParams(0);

      const url = `${API_BASE}/gigs?${params.toString()}`;

      devLog('🔍 Søker oppdrag:', { searchTerm: debouncedSearch, url });
      gigsFetchAbortRef.current?.abort();
      const controller = new AbortController();
      gigsFetchAbortRef.current = controller;

      const response = await fetch(url, accessToken
        ? {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          }
        : {
            signal: controller.signal,
          });

      if (!response.ok) {
        devLog('❌ Error fetching gigs:', await response.text());
        return;
      }

      const data = await response.json();
      if (requestId !== gigsFetchRequestIdRef.current) return;
      devLog('✅ Mottok oppdrag:', { count: data.gigs?.length || 0 });
      
      setGigs((prevGigs) => reconcileGigList(prevGigs, data.gigs || []));
      setHasMore(data.hasMore || false);
      
      // Fetch user's interests if logged in
      if (accessToken) {
        void fetchUserInterests();
      }
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return;
      devLog('❌ Error fetching gigs:', error);
    } finally {
      if (requestId === gigsFetchRequestIdRef.current) {
        setLoading(false);
        setRefreshingList(false);
      }
    }
  }

  async function loadMoreGigs() {
    const requestId = gigsFetchRequestIdRef.current;
    setLoadingMore(true);
    try {
      const params = buildGigsSearchParams(gigs.length);

      const url = `${API_BASE}/gigs?${params.toString()}`;

      devLog('📄 Laster mer oppdrag:', { offset: gigs.length, url });
      gigsLoadMoreAbortRef.current?.abort();
      const controller = new AbortController();
      gigsLoadMoreAbortRef.current = controller;

      const response = await fetch(url, accessToken
        ? {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          }
        : {
            signal: controller.signal,
          });

      if (!response.ok) {
        devLog('❌ Error fetching gigs:', await response.text());
        setLoadingMore(false);
        return;
      }

      const data = await response.json();
      if (requestId !== gigsFetchRequestIdRef.current) return;
      devLog('✅ Mottok oppdrag:', { count: data.gigs?.length || 0 });
      
      setGigs(prevGigs => [...prevGigs, ...(data.gigs || [])]);
      setHasMore(data.hasMore || false);
    } catch (error) {
      if ((error as any)?.name === 'AbortError') return;
      devLog('❌ Error fetching gigs:', error);
    } finally {
      if (requestId === gigsFetchRequestIdRef.current) {
        setLoadingMore(false);
      }
    }
  }

  async function fetchUserInterests(force: boolean = false) {
    if (!accessToken) return;
    if (gigInterestsFetchInFlightRef.current) return;
    const now = Date.now();
    if (!force && now - lastGigInterestsFetchAtRef.current < GIG_INTERESTS_FETCH_COOLDOWN_MS) return;
    gigInterestsFetchInFlightRef.current = true;
    lastGigInterestsFetchAtRef.current = now;
    
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/my-interests`
      );

      if (response.ok) {
        const data = await response.json();
        const interestsMap: Record<string, boolean> = {};
        data.interests?.forEach((interest: any) => {
          interestsMap[interest.gigId] = true;
        });
        setGigInterests(interestsMap);
      }
    } catch (error) {
      // Optional data: silent fail on auth/session errors (user may have expired session)
      devLog('Error fetching user interests:', error);
    } finally {
      gigInterestsFetchInFlightRef.current = false;
    }
  }

  const openGigDetailById = useCallback(async (gigId: string) => {
    const listGig = gigs.find((g) => g.id === gigId) ?? null;
    let gigData = listGig;

    // Open immediately with list data to remove click-to-modal latency.
    if (listGig) {
      setSelectedGig(listGig);
      setShowGigDetailModal(true);
    }

    const requestId = ++gigDetailFetchRequestIdRef.current;

    try {
      const response = await fetch(
        `${API_BASE}/gigs/${gigId}`,
        accessToken
          ? {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          : undefined
      );

      if (response.ok) {
        const data = await response.json();
        // Prefer fresh details payload to avoid stale list data (e.g. missing imageUrl).
        gigData = data.gig ?? gigData;
        if (requestId !== gigDetailFetchRequestIdRef.current) return;
      } else if (!gigData) {
        return;
      }
    } catch {
      if (!gigData) return;
    }

    if (requestId !== gigDetailFetchRequestIdRef.current) return;
    if (!gigData) return;
    setSelectedGig(gigData);
    setShowGigDetailModal(true);
  }, [accessToken, gigs]);

  useEffect(() => {
    const handleOpenGigDetail = (event: Event) => {
      const customEvent = event as CustomEvent<{ gigId?: string }>;
      const gigId = customEvent.detail?.gigId;
      if (!gigId) return;
      void openGigDetailById(gigId);
    };

    window.addEventListener('talentfunn:open-gig-detail', handleOpenGigDetail as EventListener);
    return () => {
      window.removeEventListener('talentfunn:open-gig-detail', handleOpenGigDetail as EventListener);
    };
  }, [openGigDetailById]);

  const createGigModalEl = showCreateGigModal ? (
    <CreateGigModal
      gig={selectedGig}
      onClose={() => {
        setShowCreateGigModal(false);
        setSelectedGig(null);
      }}
      onSuccess={() => {
        setShowCreateGigModal(false);
        setSelectedGig(null);
        hasFetchedInitial.current = false;
        fetchGigs(true, true);
      }}
      userProfile={userProfile}
      accessToken={accessToken || ''}
      onNavigateToSubscription={onNavigateToSubscription}
    />
  ) : null;

  const gigDetailModalEl = showGigDetailModal && selectedGig ? (
    <GigDetailModal
      gig={selectedGig}
      hasInterest={gigInterests[selectedGig.id] || false}
      isOwnGig={!!currentUserId && (selectedGig.poster?.id === currentUserId || selectedGig.employerId === currentUserId)}
      onClose={() => {
        setShowGigDetailModal(false);
        setSelectedGig(null);
      }}
      onShowInterest={(gigId) => {
        setInterestGigId(gigId);
        setInterestGigTitle(selectedGig.title);
        setShowInterestModal(true);
      }}
      onViewProfile={onViewProfile}
      onEdit={() => {
        setShowGigDetailModal(false);
        setShowCreateGigModal(true);
      }}
      onCloseGig={async (gigId) => {
        if (!accessToken) return;
        try {
          const response = await fetch(
            `${API_BASE}/gigs/${gigId}/close`,
            {
              method: 'PATCH',
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
          if (response.ok) {
            toast.success('Oppdraget er tatt ned fra søk');
            setShowGigDetailModal(false);
            setSelectedGig(null);
            hasFetchedInitial.current = false;
            fetchGigs(true, true);
          } else {
            const data = await response.json();
            toast.error(data.error || 'Kunne ikke avpublisere oppdrag');
          }
        } catch (error) {
          toast.error('Kunne ikke avpublisere oppdrag');
        }
      }}
      onPublishGig={async (gigId) => {
        if (!accessToken) return;
        try {
          const response = await fetch(
            `${API_BASE}/gigs/${gigId}/publish`,
            {
              method: 'PATCH',
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
          if (response.ok) {
            toast.success('Oppdraget er publisert igjen');
            setShowGigDetailModal(false);
            setSelectedGig(null);
            hasFetchedInitial.current = false;
            fetchGigs(true, true);
          } else {
            const data = await response.json();
            toast.error(data.error || 'Kunne ikke publisere oppdrag');
          }
        } catch (error) {
          toast.error('Kunne ikke publisere oppdrag');
        }
      }}
      onDeleteGig={async (gigId) => {
        if (!accessToken) return;
        try {
          const response = await fetch(
            `${API_BASE}/gigs/${gigId}`,
            {
              method: 'DELETE',
              headers: { Authorization: `Bearer ${accessToken}` },
            }
          );
          if (response.ok) {
            toast.success('Oppdraget er slettet');
            setShowGigDetailModal(false);
            setSelectedGig(null);
            hasFetchedInitial.current = false;
            fetchGigs(true, true);
          } else {
            const data = await response.json();
            toast.error(data.error || 'Kunne ikke slette oppdrag');
          }
        } catch (error) {
          toast.error('Kunne ikke slette oppdrag');
        }
      }}
      onViewInterests={(gigId) => {
        if (onOpenGigInterests && selectedGig) {
          setShowGigDetailModal(false);
          onOpenGigInterests(gigId, selectedGig.title);
        }
      }}
    />
  ) : null;

  if (showCreateGigModal && !isDesktop) {
    return createGigModalEl;
  }

  if (showGigDetailModal && selectedGig && !isDesktop) {
    return (
      <>
        {showCreateGigModal && isDesktop && createGigModalEl && ReactDOM.createPortal(createGigModalEl, document.body)}
        {gigDetailModalEl}
        {showInterestModal && interestGigId && accessToken && ReactDOM.createPortal(
          <ShowInterestModal
            gigId={interestGigId}
            gigTitle={interestGigTitle}
            onClose={() => {
              setShowInterestModal(false);
              setInterestGigId('');
              setInterestGigTitle('');
            }}
            onSuccess={() => {
              void fetchUserInterests(true);
              setShowGigDetailModal(false);
              setSelectedGig(null);
            }}
            userSubscription={userProfile?.subscription || ''}
            userType={userProfile?.userType}
            accessToken={accessToken}
            onNavigateToSubscription={onNavigateToSubscription}
          />,
          document.body
        )}
      </>
    );
  }

  return (
    <>
      {showCreateGigModal && isDesktop && createGigModalEl && ReactDOM.createPortal(createGigModalEl, document.body)}
      {showGigDetailModal && selectedGig && isDesktop && gigDetailModalEl && ReactDOM.createPortal(gigDetailModalEl, document.body)}
      {showInterestModal && interestGigId && accessToken && ReactDOM.createPortal(
        <ShowInterestModal
          gigId={interestGigId}
          gigTitle={interestGigTitle}
          onClose={() => {
            setShowInterestModal(false);
            setInterestGigId('');
            setInterestGigTitle('');
          }}
          onSuccess={() => {
            void fetchUserInterests(true);
            setShowGigDetailModal(false);
            setSelectedGig(null);
          }}
          userSubscription={userProfile?.subscription || ''}
          userType={userProfile?.userType}
          accessToken={accessToken}
          onNavigateToSubscription={onNavigateToSubscription}
        />,
        document.body
      )}
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <PageShell className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 pt-5 xl:pt-5 xl:pb-24">
          {/* Title only – scrolls away when user scrolls down */}
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl xl:text-5xl bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 bg-clip-text text-transparent pb-2">
              {headerText}
            </h1>
          </div>

          {/* Sentinel: marks the natural position of the search bar for iOS PWA IntersectionObserver */}
          <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />

          {/* Sticky search bar – sticks under global header when scrolling.
              iOS PWA: .is-stuck toggles to position:fixed via JS (position:sticky broken in standalone). */}
          <div ref={stickyBarRef} className="sticky-search-bar sticky top-0 z-[var(--z-sticky)] -mx-4 px-4 sm:-mx-6 sm:px-6 xl:-mx-8 xl:px-8 pt-2 pb-4 mb-8">
            <div className="max-w-2xl mx-auto">
              <div className="relative group">
                {/* Gradient glow effect */}
                <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
                
                {/* Search bar */}
                <div className="relative bg-white rounded-2xl shadow-xl flex items-center gap-2">
                  <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 text-purple-400 w-6 h-6 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        searchInputRef.current?.blur();
                      }
                    }}
                    placeholder="Søk etter oppdrag..."
                    className="w-full pl-16 pr-16 py-5 rounded-2xl focus:outline-none focus:ring-4 focus:ring-purple-300 text-lg transition-all duration-150"
                    ref={searchInputRef}
                  />
                  
                  {/* County filter button - hidden when modals are open */}
                  {!showCreateGigModal && <div className="absolute right-2 top-1/2 transform -translate-y-1/2 z-[100]" ref={countyDropdownRef}>
                    <motion.button
                      onClick={() => setShowCountyDropdown(!showCountyDropdown)}
                      className={`w-11 h-11 flex items-center justify-center rounded-lg transition-colors duration-150 ${
                        showCountyDropdown 
                          ? 'bg-purple-100 text-purple-600' 
                          : 'text-purple-400 hover:bg-purple-50'
                      }`}
                      whileTap={{ scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    >
                      <AnimatedFilterIcon size={24} ref={filterIconRef} />
                    </motion.button>
                    
                    {/* Dropdown menu */}
                    <AnimatePresence>
                      {showCountyDropdown && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95, y: -10 }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{ opacity: 0, scale: 0.95, y: -10 }}
                          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                          className="absolute right-0 mt-2 w-64 origin-top-right rounded-xl bg-white shadow-lg border border-purple-100 focus:outline-none z-[10000] max-h-96 overflow-y-auto"
                        >
                          <div className="p-2">
                            <div className="px-3 py-2.5 text-xs font-medium text-purple-600 border-b border-purple-100 mb-1">
                              {selectedCounties.length > 0 ? (
                                <button
                                  onClick={() => setSelectedCounties([])}
                                  className="text-purple-600 hover:text-purple-700 font-semibold"
                                >
                                  Nullstill alle
                                </button>
                              ) : (
                                'Velg ett eller flere fylker'
                              )}
                            </div>
                            {NORWEGIAN_COUNTIES.map((county) => (
                              <label
                                key={county}
                                className="flex items-center px-3 py-2.5 text-sm text-gray-700 hover:bg-purple-50 rounded-lg cursor-pointer transition-colors duration-150"
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedCounties.includes(county)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedCounties([...selectedCounties, county]);
                                    } else {
                                      setSelectedCounties(selectedCounties.filter((c) => c !== county));
                                    }
                                  }}
                                  className="mr-3 w-4 h-4 text-purple-600 border-purple-300 rounded focus:ring-2 focus:ring-purple-500 accent-purple-600"
                                />
                                <span className="flex-1">{county}</span>
                                {selectedCounties.includes(county) && (
                                  <Check className="w-4 h-4 text-purple-600 shrink-0" strokeWidth={2.5} />
                                )}
                              </label>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>}
                </div>
              </div>
              {canOpenMyInterests && onOpenMyInterests && (
                <div className="mt-3 flex">
                  <button
                    onClick={onOpenMyInterests}
                    className="w-full sm:w-auto sm:ml-auto xl:w-full xl:ml-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-purple-200 bg-white/90 text-purple-700 hover:bg-purple-50 hover:border-purple-300 transition-all duration-150 active:scale-[0.98] font-medium shadow-sm"
                  >
                    <NotebookText className="w-4 h-4" />
                    Aktuelle oppdrag
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* iOS PWA: placeholder to prevent content jump when search bar switches to position:fixed.
              Always rendered (height toggled via DOM to avoid React re-render feedback loop). */}
          <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />

          {/* Gigs grid */}
          {loading ? (
            <div className="text-center py-12">
              <Loader size="xl" className="text-purple-600 mx-auto" />
              <p className="mt-4 text-gray-700">Finner oppdrag...</p>
            </div>
          ) : gigs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md p-8">
              <div className="max-w-md mx-auto">
                <div className="flex justify-center mb-4">
                  <Briefcase className="w-16 h-16 text-gray-400" />
                </div>
                <h3 className="text-2xl mb-3 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                  Ingen oppdrag ennå
                </h3>
                <p className="text-gray-600 mb-6">
                  {searchQuery || selectedCounties.length > 0
                    ? 'Ingen oppdrag matcher søket ditt. Prøv andre søkeord.'
                    : 'Vær den første til å publisere et oppdrag!'}
                </p>
                {canCreateGig(userProfile) && (
                  <button
                    onClick={() => setShowCreateGigModal(true)}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold inline-flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Opprett oppdrag
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-6">
                {enableWindowVirtualization ? (
                  <div className="xl:col-span-2 2xl:col-span-3">
                    <div
                      style={{
                        height: `${gigsVirtualizer.getTotalSize()}px`,
                        width: '100%',
                        position: 'relative',
                      }}
                    >
                      {gigsVirtualizer.getVirtualItems().map((virtualRow) => {
                        const gig = gigsForRender[virtualRow.index];
                        if (!gig) return null;
                        return (
                          <div
                            key={gig.id}
                            ref={gigsVirtualizer.measureElement}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              transform: `translateY(${virtualRow.start}px)`,
                              paddingBottom: '24px',
                            }}
                          >
                            <GigCard
                              gig={gig}
                              hasInterest={gigInterests[gig.id] || false}
                              onViewDetails={(gigId) => {
                                void openGigDetailById(gigId);
                              }}
                              onShowInterest={(gigId) => {
                                const gigData = gigs.find(g => g.id === gigId);
                                setInterestGigId(gigId);
                                setInterestGigTitle(gigData?.title || '');
                                setShowInterestModal(true);
                              }}
                              onViewProfile={onViewProfile}
                              isOwnGig={!!currentUserId && (gig.poster?.id === currentUserId || gig.employerId === currentUserId)}
                              onViewInterests={onOpenGigInterests}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  visibleGigs.map((gig: any) => (
                    <GigCard
                      key={gig.id}
                      gig={gig}
                      hasInterest={gigInterests[gig.id] || false}
                      onViewDetails={(gigId) => {
                        void openGigDetailById(gigId);
                      }}
                      onShowInterest={(gigId) => {
                        const gigData = gigs.find(g => g.id === gigId);
                        setInterestGigId(gigId);
                        setInterestGigTitle(gigData?.title || '');
                        setShowInterestModal(true);
                      }}
                      onViewProfile={onViewProfile}
                      isOwnGig={!!currentUserId && (gig.poster?.id === currentUserId || gig.employerId === currentUserId)}
                      onViewInterests={onOpenGigInterests}
                    />
                  ))
                )}
              </div>

              {/* Load more indicator */}
              {hasMore && (
                <div
                  ref={loadMoreRef}
                  className="text-center py-8 mt-6"
                >
                  {loadingMore ? (
                    <Loader size="xl" className="text-purple-600" />
                  ) : (
                    <button
                      onClick={loadMoreGigs}
                      className="px-6 py-3 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-all duration-150 active:scale-[0.98] font-semibold"
                    >
                      Last inn flere oppdrag
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </PageShell>

      {/* FAB for creating gigs (Employer Plus only) */}
      {accessToken && canCreateGig(userProfile) && (
        <button
          onClick={() => setShowCreateGigModal(true)}
          className="fixed bottom-24 right-4 xl:bottom-8 w-14 h-14 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full shadow-lg hover:scale-105 active:scale-95 transition-transform duration-150 z-50 flex items-center justify-center fab-bottom-safe"
          title="Opprett nytt oppdrag"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
      </div>
    </>
  );
}
